import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import language from '../../variables/language';
import usePersistedState from '../../hooks/usePersistedState';
import IconGoBack from '../../components/icons/IconGoBack';
import IconMessages from '../../components/icons/IconMessages';

// some legacy dAdd values carry a trailing IANA zone in brackets luxon
// can't parse directly (e.g. "...+02:00[Europe/Warsaw]") - same helper as
// StudentsConversations.jsx.
const formatDate = (val) => {
  if (!val) return '';
  const dt = DateTime.fromISO(val.replace(/\[.*\]/, ''), { setZone: true });
  return dt.isValid ? dt.toFormat('dd.MM.yyyy HH:mm') : '';
};

// bare=true skips the own outer box+title (see GroupMembersPanel.jsx for
// why - same GroupSidebarTabs.jsx use case).
export default function GroupTestsWidget({ groupId, refresh, bare }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  // Persisted (not just local useState) so that clicking through to the
  // full-screen /messages/byconvprogress/:id view and then using the
  // browser's own back button lands back on this same group tab with the
  // same student's conversation list still expanded, instead of resetting
  // to the summary list - this is what the user means by "remembers where
  // you came from". Scoped per group since a teacher may have several
  // group tabs open across sessions.
  const [expandedStudentId, setExpandedStudentId] = usePersistedState('group-tests-expanded-' + groupId, null);

  useEffect(() => {
    axios.get('/api/students?groupe=' + groupId, { withCredentials: true })
      .then(response => {
        const students = response.data || [];
        Promise.all(students.map(student =>
          axios.get('/api/conversationprogress?student=' + student.id + '&isForMe=1', { withCredentials: true })
            .then(r => ({ student, progresses: r.data || [] }))
            .catch(() => ({ student, progresses: [] }))
        )).then(setRows);
      })
      .catch(() => setRows([]));
  }, [groupId, refresh]);

  const lang = language[language.lang];
  const expandedRow = rows.find(r => r.student.id === expandedStudentId);

  // the persisted student id can point at someone no longer in the group
  // (removed, or a stale value from a previous group reusing this id) -
  // fall back to the summary list rather than getting stuck on nothing.
  useEffect(() => {
    if (rows.length > 0 && expandedStudentId && !expandedRow) {
      setExpandedStudentId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, expandedStudentId]);

  const content = expandedRow ? (
    <div className="group-tests-detail">
      <div className="group-tests-detail-header" onClick={() => setExpandedStudentId(null)} title={lang.groupCard.backToTests}>
        <IconGoBack style={{ width: 16, height: 16, margin: 0 }} />
        <span className="group-tests-detail-name">{expandedRow.student.name} {expandedRow.student.surname}</span>
      </div>
      {expandedRow.progresses.length === 0 ? (
        <p className="group-attendance-empty">{lang.groupCard.noTests}</p>
      ) : (
        <ul className="group-tests-list">
          {expandedRow.progresses.map(item => (
            <li className="group-tests-conv-row" key={item.id}
              onClick={() => navigate('/messages/byconvprogress/' + item.id)}>
              <span className="group-tests-name">{item.conversationId ? item.conversationId.name : ''}</span>
              <span className="group-tests-conv-meta">
                <span className="group-tests-conv-date">{formatDate(item.dAdd)}</span>
                <span className={'conv-participant-status ' + (item.isFinished === 1 ? 'done' : 'pending')}>
                  {item.isFinished === 1 ? lang.isFinished : lang.inProgress}
                </span>
              </span>
              <IconMessages style={{ fill: '#7ec2e8', width: 13, height: 13, margin: 0, flex: 'none' }} />
            </li>
          ))}
        </ul>
      )}
    </div>
  ) : rows.length === 0 ? (
    <p className="group-attendance-empty">{lang.groupCard.noTests}</p>
  ) : (
    <ul className="group-tests-list">
      {rows.map(({ student, progresses }) => {
        const finished = progresses.filter(p => p.isFinished === 1).length;
        const pending = progresses.length - finished;
        return (
          <li className="group-tests-row" key={student.id} onClick={() => setExpandedStudentId(student.id)}>
            <span className="group-tests-name">{student.name} {student.surname}</span>
            <span className="group-tests-counts">
              <span className="conv-participant-status done">{finished} {lang.isFinished}</span>
              {pending > 0 ? (
                <span className="conv-participant-status pending">{pending} {lang.groupCard.pendingStatus}</span>
              ) : ''}
            </span>
          </li>
        );
      })}
    </ul>
  );

  if (bare) {
    return content;
  }

  return (
    <div className="group-card-section">
      <h4 className="group-card-section-title">{lang.groupCard.testsTitle}</h4>
      {content}
    </div>
  );
}
